import React, { useEffect, useRef, useState } from 'react'
import { Box, IconButton, InputBase, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import MicNoneRoundedIcon from '@mui/icons-material/MicNoneRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import { isMacOS } from '../../../util/general'
import { useDictationTarget } from './dictationHotkey'
import useSpeechToText from './useSpeechToText'
import VoiceRecordingBar from './VoiceRecordingBar'

type Props = {
    input: string
    onInputChange: (value: string) => void
    onSend: () => void
    onAbort: () => void
    loading: boolean
    canStop: boolean
    disabled: boolean
    placeholder?: string
}

const ACTION_SIZE = 26
const DICTATION_KEY_LABEL = isMacOS() ? 'right ⌥' : 'right Alt'

function enabledActionSx(isDark: boolean) {
    return {
        width: ACTION_SIZE,
        height: ACTION_SIZE,
        borderRadius: 1,
        bgcolor: isDark ? 'common.white' : 'grey.900',
        color: isDark ? 'grey.900' : 'common.white',
        '&:hover': {
            bgcolor: isDark ? 'grey.200' : 'grey.800',
        },
    }
}

export default function ChatComposer({
    input,
    onInputChange,
    onSend,
    onAbort,
    loading,
    canStop,
    disabled,
    placeholder = 'Ask e.g. stocks with RSI(14) > 70',
}: Props) {
    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'
    const showStop = loading && canStop
    const canSend = !!input.trim() && !disabled && !loading
    const inputRef = useRef<HTMLTextAreaElement>(null)

    // Input before dictation (restored on cancel), and the prefix dictation is appended to.
    const dictationOriginalRef = useRef('')
    const dictationBaseRef = useRef('')
    const [interim, setInterim] = useState('')
    const speech = useSpeechToText(({ final, interim }) => {
        onInputChange(dictationBaseRef.current + final)
        setInterim(interim)
    })
    const recording = speech.status !== 'idle'
    const micDisabled = disabled || loading

    const startDictation = () => {
        if (!speech.supported || micDisabled || recording) return
        dictationOriginalRef.current = input
        dictationBaseRef.current = input.trim() ? `${input.trimEnd()} ` : ''
        setInterim('')
        speech.start()
    }

    const cancelDictation = () => {
        speech.cancel()
        onInputChange(dictationOriginalRef.current)
    }

    useDictationTarget({
        start: startDictation,
        stop: speech.stop,
        cancel: cancelDictation,
        toggle: () => (recording ? speech.stop() : startDictation()),
    })

    useEffect(() => {
        if (micDisabled) speech.cancel()
    }, [micDisabled, speech.cancel])

    // Back to the text box once dictation ends, caret at the end, ready to edit or send.
    const wasRecordingRef = useRef(false)
    useEffect(() => {
        if (wasRecordingRef.current && !recording) {
            setInterim('')
            const el = inputRef.current
            if (el) {
                el.focus()
                el.setSelectionRange(el.value.length, el.value.length)
            }
        }
        wasRecordingRef.current = recording
    }, [recording])

    const cancelRef = useRef(cancelDictation)
    cancelRef.current = cancelDictation
    useEffect(() => {
        if (!recording) return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
            if (e.key === 'Enter') speech.stop()
            else if (e.key === 'Escape') cancelRef.current()
            else return
            e.preventDefault()
            e.stopPropagation()
        }
        window.addEventListener('keydown', onKeyDown, true)
        return () => window.removeEventListener('keydown', onKeyDown, true)
    }, [recording, speech.stop])

    const send = () => {
        speech.cancel()
        onSend()
    }

    const needsSpace = input && interim && !/\s$/.test(input)

    return (
        <Box
            sx={{
                flexShrink: 0,
                // Same inset on every side so the box sits square in the panel.
                px: 1.25,
                py: 1.25,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <Stack
                direction={recording ? 'column' : 'row'}
                alignItems={recording ? 'stretch' : 'center'}
                spacing={recording ? 0.5 : 1}
                sx={{
                    border: 1,
                    borderColor: recording ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.625,
                    minHeight: ACTION_SIZE + 12,
                    bgcolor: 'background.default',
                    transition: 'border-color 0.15s',
                    '&:focus-within': { borderColor: 'primary.main' },
                }}
            >
                {recording ? (
                    <>
                        <Typography
                            component="div"
                            aria-live="polite"
                            sx={{
                                fontSize: '0.875rem',
                                lineHeight: 1.5,
                                py: '6px',
                                maxHeight: '7.5em',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {input || interim ? (
                                <>
                                    {input}
                                    {needsSpace ? ' ' : ''}
                                    <Box component="span" sx={{ color: 'text.disabled' }}>
                                        {interim}
                                    </Box>
                                </>
                            ) : (
                                <Box component="span" sx={{ color: 'text.disabled' }}>
                                    {speech.status === 'listening' ? 'Listening…' : 'Starting mic…'}
                                </Box>
                            )}
                        </Typography>
                        <VoiceRecordingBar
                            live={speech.status === 'listening'}
                            onCancel={cancelDictation}
                            onDone={speech.stop}
                            doneSx={enabledActionSx(isDark)}
                            size={ACTION_SIZE}
                        />
                    </>
                ) : (
                    <>
                        <InputBase
                            fullWidth
                            multiline
                            maxRows={4}
                            placeholder={placeholder}
                            value={input}
                            inputRef={inputRef}
                            onChange={(e) => onInputChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault()
                                    if (canSend) {
                                        send()
                                    }
                                }
                            }}
                            disabled={disabled}
                            sx={{
                                flex: 1,
                                alignSelf: input.includes('\n') ? 'flex-end' : 'center',
                                px: 0,
                                py: 0,
                                fontSize: '0.875rem',
                                lineHeight: 1.5,
                                '& textarea': {
                                    resize: 'none',
                                    padding: '6px 0',
                                },
                            }}
                        />
                        {speech.supported && (
                            <Tooltip title={speech.error ?? `Dictate · hold ${DICTATION_KEY_LABEL}`}>
                                <span>
                                    <IconButton
                                        onClick={startDictation}
                                        disabled={micDisabled}
                                        aria-label="Dictate"
                                        size="small"
                                        sx={{
                                            flexShrink: 0,
                                            alignSelf: input.includes('\n') ? 'flex-end' : 'center',
                                            width: ACTION_SIZE,
                                            height: ACTION_SIZE,
                                            borderRadius: 1,
                                            color: speech.error ? 'error.main' : 'text.secondary',
                                        }}
                                    >
                                        <MicNoneRoundedIcon sx={{ fontSize: 17 }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                        {showStop ? (
                            <Tooltip title="Stop generating">
                                <IconButton
                                    onClick={onAbort}
                                    aria-label="Stop generating"
                                    size="small"
                                    sx={{
                                        flexShrink: 0,
                                        alignSelf: input.includes('\n') ? 'flex-end' : 'center',
                                        ...enabledActionSx(isDark),
                                        borderRadius: '50%',
                                    }}
                                >
                                    <StopRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Tooltip title={canSend ? 'Send · Enter' : 'Type a message'}>
                                <span>
                                    <IconButton
                                        onClick={send}
                                        disabled={!canSend}
                                        aria-label="Send"
                                        size="small"
                                        sx={{
                                            flexShrink: 0,
                                            alignSelf: input.includes('\n') ? 'flex-end' : 'center',
                                            width: ACTION_SIZE,
                                            height: ACTION_SIZE,
                                            borderRadius: 1,
                                            ...(canSend
                                                ? enabledActionSx(isDark)
                                                : {
                                                      bgcolor: 'action.disabledBackground',
                                                      color: 'action.disabled',
                                                      '&:hover': { bgcolor: 'action.disabledBackground' },
                                                      '&.Mui-disabled': {
                                                          bgcolor: 'action.disabledBackground',
                                                          color: 'action.disabled',
                                                      },
                                                  }),
                                        }}
                                    >
                                        <ArrowUpwardRoundedIcon sx={{ fontSize: 15 }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </>
                )}
            </Stack>
        </Box>
    )
}
